import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Lock } from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const PrivacyPolicy = () => {
  const sections = [
    { id: '1', title: '1. Introduction', href: '#introduction' },
    { id: '2', title: '2. Information We Collect', href: '#collect' },
    { id: '3', title: '3. How We Use Your Information', href: '#use' },
    { id: '4', title: '4. Legal Basis for Processing', href: '#legal-basis' },
    { id: '5', title: '5. Sharing Your Information', href: '#sharing' },
    { id: '6', title: '6. International Transfers', href: '#transfers' },
    { id: '7', title: '7. Data Security', href: '#security' },
    { id: '8', title: '8. Data Retention', href: '#retention' },
    { id: '9', title: '9. Your Rights', href: '#rights' },
    { id: '10', title: '10. Cookies', href: '#cookies' },
    { id: '11', title: '11. Changes to This Policy', href: '#changes' },
    { id: '12', title: '12. Contact Us', href: '#contact' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageTitle title="Privacy Policy" />
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span>Privacy Policy</span>
          </div>
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/10 p-3 rounded-lg">
              <Shield className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif">Privacy Policy</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-3xl">
            At Ashbourne Law, we are committed to protecting your privacy and ensuring the security of your personal information.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={section.href}
                    className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="prose prose-lg max-w-none">
              {/* Section 1 */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">1. Introduction</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Ashbourne Law Limited ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you engage our legal services or visit our website.
                  </p>
                  <p>
                    This policy should be read together with our <Link to="/terms-of-engagement" className="text-gray-900 underline">Terms of Engagement</Link> and <Link to="/cookie-policy" className="text-gray-900 underline">Cookie Policy</Link>.
                  </p>
                  <p>
                    <strong>Data Controller:</strong> Ashbourne Law Limited, 45 Gresham Street, London EC2V 7BG, United Kingdom.
                  </p>
                  <p>
                    <strong>Contact:</strong> For any questions about this Privacy Policy or our data processing practices, please contact our Privacy Manager at <a href="mailto:privacy@ashbournelaw.com" className="text-gray-900 underline">privacy@ashbournelaw.com</a>.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="collect" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">2. Information We Collect</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We collect and process various types of personal information depending on the nature of our relationship with you:
                  </p>
                  
                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.1 Information You Provide</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Identity information:</strong> Name, date of birth, passport or ID details, signature</li>
                    <li><strong>Contact information:</strong> Email address, telephone number, postal address</li>
                    <li><strong>Financial information:</strong> Bank account details, payment information, financial circumstances</li>
                    <li><strong>Professional information:</strong> Company details, position, business activities</li>
                    <li><strong>Legal matter information:</strong> Details about your legal matter, correspondence, documents, and any other information relevant to providing legal services</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.2 Special Category Data</h3>
                  <p>
                    In certain circumstances, we may process special category personal data (sensitive personal data), including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Racial or ethnic origin</li>
                    <li>Health information</li>
                    <li>Criminal convictions and offences</li>
                    <li>Biometric data</li>
                  </ul>
                  <p>
                    We will only process special category data where it is necessary for legal proceedings, legal advice, or where you have given explicit consent.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.3 Information We Collect Automatically</h3>
                  <p>
                    When you visit our website, we automatically collect certain information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website</li>
                    <li>Cookies and similar technologies (see our <Link to="/cookie-policy" className="text-gray-900 underline">Cookie Policy</Link>)</li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.4 Information from Third Parties</h3>
                  <p>
                    We may receive personal information from:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Other legal professionals, experts, and advisers</li>
                    <li>Courts and tribunals</li>
                    <li>Regulatory bodies</li>
                    <li>Credit reference and identity verification agencies</li>
                    <li>Publicly available sources</li>
                  </ul>
                </div>
              </section>

              {/* Section 3 */}
              <section id="use" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">3. How We Use Your Information</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We use your personal information for the following purposes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Providing legal services:</strong> To advise you, represent you, and handle your legal matters</li>
                    <li><strong>Client administration:</strong> To manage our relationship with you, including invoicing and fee collection</li>
                    <li><strong>Compliance:</strong> To comply with legal and regulatory obligations, including anti-money laundering checks, conflict checks, and professional duties</li>
                    <li><strong>Business operations:</strong> To administer our business, maintain records, and improve our services</li>
                    <li><strong>Communications:</strong> To respond to your enquiries and provide updates on your matter</li>
                    <li><strong>Marketing:</strong> With your consent, to send you information about our services (you can opt out at any time)</li>
                    <li><strong>Legal proceedings:</strong> To establish, exercise, or defend legal claims</li>
                    <li><strong>Security:</strong> To protect our systems, detect fraud, and prevent unauthorized access</li>
                  </ul>
                </div>
              </section>

              {/* Section 4 */}
              <section id="legal-basis" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">4. Legal Basis for Processing</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Under UK GDPR, we must have a legal basis for processing your personal information. We rely on the following legal bases:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Contract:</strong> Processing is necessary to perform our contract with you (i.e., to provide legal services)</li>
                    <li><strong>Legal obligation:</strong> Processing is necessary to comply with legal and regulatory requirements</li>
                    <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate business interests (e.g., managing our business, improving services, preventing fraud), provided this does not override your rights</li>
                    <li><strong>Consent:</strong> You have given explicit consent for specific processing activities (e.g., marketing communications, processing special category data)</li>
                    <li><strong>Legal claims:</strong> Processing is necessary for the establishment, exercise, or defense of legal claims</li>
                  </ul>
                </div>
              </section>

              {/* Section 5 */}
              <section id="sharing" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">5. Sharing Your Information</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We may share your personal information with:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Other parties to your matter:</strong> Opposing parties, their legal representatives, courts, and tribunals</li>
                    <li><strong>Professional advisers:</strong> Barristers, expert witnesses, foreign counsel, accountants, and other specialists engaged on your matter</li>
                    <li><strong>Service providers:</strong> IT service providers, document storage providers, payment processors, and other suppliers who support our business</li>
                    <li><strong>Regulators and authorities:</strong> The Solicitors Regulation Authority, Legal Ombudsman, courts, HMRC, National Crime Agency, and other regulatory bodies</li>
                    <li><strong>Professional advisers to the firm:</strong> Our auditors, insurers, bankers, and legal advisers</li>
                    <li><strong>Affiliated offices:</strong> Other offices of Ashbourne Law in different jurisdictions</li>
                  </ul>
                  <p>
                    We require all third parties to respect the security of your data and treat it in accordance with the law. We only permit them to process your data for specified purposes and in accordance with our instructions.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section id="transfers" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">6. International Transfers</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    As an international law firm, we may transfer your personal information to countries outside the United Kingdom and European Economic Area (EEA). This may occur when:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Your matter involves parties, assets, or proceedings in other jurisdictions</li>
                    <li>We engage foreign counsel or experts</li>
                    <li>We use cloud-based services with international infrastructure</li>
                  </ul>
                  <p>
                    Where we transfer data internationally, we ensure appropriate safeguards are in place, such as:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Transfers to countries with adequacy decisions from the UK government</li>
                    <li>Standard contractual clauses approved by the UK Information Commissioner's Office</li>
                    <li>Binding corporate rules or codes of conduct</li>
                  </ul>
                  <p>
                    You can request further information about the safeguards we use by contacting our Privacy Manager.
                  </p>
                </div>
              </section>

              {/* Section 7 */}
              <section id="security" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">7. Data Security</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and penetration testing</li>
                    <li>Access controls and authentication procedures</li>
                    <li>Staff training on data protection and security</li>
                    <li>Secure disposal of physical and electronic records</li>
                    <li>Incident response and business continuity plans</li>
                  </ul>
                  <p>
                    While we strive to protect your personal information, no method of transmission or storage is completely secure. You should take steps to protect your own information, such as using strong passwords and keeping login credentials confidential.
                  </p>
                </div>
              </section>

              {/* Section 8 */}
              <section id="retention" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">8. Data Retention</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Client files:</strong> We retain client files for a minimum of seven years after the matter concludes, or longer if required by law or professional rules. For certain matters (e.g., involving minors, property transactions), we may retain files for up to 15 years or more.</li>
                    <li><strong>Financial records:</strong> We retain financial and accounting records for at least six years to comply with tax and accounting regulations.</li>
                    <li><strong>Anti-money laundering records:</strong> We retain identity verification records for at least five years after our relationship ends.</li>
                    <li><strong>Marketing data:</strong> We retain marketing consent records until you withdraw consent or we no longer have a legitimate reason to retain them.</li>
                  </ul>
                  <p>
                    After the retention period expires, we securely destroy personal information in accordance with our data retention policy.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section id="rights" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">9. Your Rights</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Under UK data protection law, you have the following rights:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Right of access:</strong> You can request a copy of the personal information we hold about you.</li>
                    <li><strong>Right to rectification:</strong> You can ask us to correct inaccurate or incomplete personal information.</li>
                    <li><strong>Right to erasure:</strong> In certain circumstances, you can request deletion of your personal information.</li>
                    <li><strong>Right to restrict processing:</strong> You can ask us to restrict how we use your personal information.</li>
                    <li><strong>Right to data portability:</strong> You can request a copy of your data in a structured, machine-readable format.</li>
                    <li><strong>Right to object:</strong> You can object to processing based on legitimate interests or for direct marketing purposes.</li>
                    <li><strong>Rights related to automated decision-making:</strong> You have rights regarding decisions made solely by automated means.</li>
                    <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you can withdraw it at any time.</li>
                  </ul>
                  <p>
                    Please note that these rights are subject to certain exemptions and limitations, particularly where processing is necessary for legal proceedings or compliance with legal obligations.
                  </p>
                  <p>
                    To exercise any of these rights, please contact our Privacy Manager at <a href="mailto:privacy@ashbournelaw.com" className="text-gray-900 underline">privacy@ashbournelaw.com</a>. We will respond within one month.
                  </p>
                  <p>
                    You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe we have not handled your data properly. Visit <a href="https://ico.org.uk" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a> for more information.
                  </p>
                </div>
              </section>

              {/* Section 10 */}
              <section id="cookies" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">10. Cookies and Tracking Technologies</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Our website uses cookies and similar tracking technologies to enhance your browsing experience and analyze website usage.
                  </p>
                  <p>
                    For detailed information about the cookies we use and how to manage your preferences, please see our <Link to="/cookie-policy" className="text-gray-900 underline">Cookie Policy</Link>.
                  </p>
                </div>
              </section>

              {/* Section 11 */}
              <section id="changes" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">11. Changes to This Policy</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website with a revised "Last Updated" date.
                  </p>
                  <p>
                    We encourage you to review this Privacy Policy periodically. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.
                  </p>
                </div>
              </section>

              {/* Section 12 */}
              <section id="contact" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">12. Contact Us</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    If you have any questions, concerns, or requests regarding this Privacy Policy or our data processing practices, please contact:
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-4">
                    <p className="font-medium text-gray-900">Privacy Manager</p>
                    <p className="text-gray-700 mt-2">Ashbourne Law Limited</p>
                    <p className="text-gray-700">45 Gresham Street</p>
                    <p className="text-gray-700">London EC2V 7BG</p>
                    <p className="text-gray-700">United Kingdom</p>
                    <p className="text-gray-700 mt-3">
                      Email: <a href="mailto:privacy@ashbournelaw.com" className="text-gray-900 underline">privacy@ashbournelaw.com</a>
                    </p>
                    <p className="text-gray-700">
                      Phone: <a href="tel:+442071234567" className="text-gray-900 underline">+44 20 7123 4567</a>
                    </p>
                  </div>
                </div>
              </section>

              <p className="text-sm text-gray-500 mt-8">
                Last updated: December 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;